import { ChangeDetectorRef, Component, HostListener, OnInit } from "@angular/core";
import { ColDef, GridApi, GridOptions, GridReadyEvent, SelectionChangedEvent } from "ag-grid-community";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { ToastrService } from "ngx-toastr";
import { ApiService } from "../services";
import { LoadingScreenService } from "../shared/loading-screen.service";
import { GridViewBaseComponent } from "../utility/grid-view-base.component";
import { SharedDataService } from "../services/sharedDataService";
import { Blueprint } from "../generated-models/Blueprint";
import { Demand } from "../generated-models/Demand";
import { Project } from "../generated-models/Project";
import { extendDefaultOptions, agDateColumnFilter } from "../utility/gridHelper";
import * as CDs from "../utility/commonColDef";
import { PreprocessDateTime } from "../utility/view-field-mapping";
import { LinkCellComponent } from "../shared/grid/link-cell.component";
import { ColParams } from "../shared/grid/ag-grid";
import { CapacityBlueprintAssignPreviewModalComponent, BlueprintPreviewItem } from "./assign-preview-modal/assign-preview-modal.component";
import { CapacityBlueprintColumnDefinition } from "./ag-grid-column-definition";

interface RowDataForBlueprint extends Blueprint {
  Assigned: boolean;
}

interface ProjectListRecord extends Project {
  Label: string;
}

@Component({
  standalone: false,
  templateUrl: "./assign-blueprint.component.html",
  styleUrls: ["../styles.scss", "./capacity-blueprint.scss"],
})
export class CapacityBlueprintAssignmentComponent extends GridViewBaseComponent implements OnInit {
  gridOptions!: GridOptions<RowDataForBlueprint>;
  gridApi!: GridApi;
  initPageSize = 15;
  keyFilterStats: string = "filter:capacity-blueprint-assign";

  projects: ProjectListRecord[] = [];
  selectedProject!: ProjectListRecord;

  blueprints: Blueprint[] = [];
  demands: Demand[] = [];
  rowData: RowDataForBlueprint[] = [];

  hasRowSelected = false;
  canAssign = false;

  constructor(
    protected apiService: ApiService,
    private loadingService: LoadingScreenService,
    protected sharedDataService: SharedDataService,
    private notificationService: ToastrService,
    private cdr: ChangeDetectorRef,
    private ngbModal: NgbModal
  ) {
    super(apiService, sharedDataService);
  }

  async ngOnInit() {
    this.hasRowSelected = false;

    const ColumnDefinition: ColDef[] = [
      {
        headerName: "",
        field: "checkbox",
        width: 50,
        maxWidth: 50,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        suppressMenu: true,
        sortable: false,
        filter: false,
      },
      ...CapacityBlueprintColumnDefinition,
      {
        headerName: "Assigned",
        field: "Assigned",
        width: 100,
        maxWidth: 100,
        cellRenderer: (params: any) => params.value ? "Yes" : "No",
      },
    ];

    this.gridOptions = extendDefaultOptions({
      columnDefs: ColumnDefinition,
      paginationPageSize: this.initPageSize,
      enableRangeSelection: false,
      rowSelection: "multiple",
      animateRows: true,
      isRowSelectable: (node) => !(node.data as RowDataForBlueprint)?.Assigned,
    });

    this.loadingService.setLoading(true);

    try {
      // Load projects
      const projectsResponse = await this.apiService.getProjects().toPromise();
      if (projectsResponse) {
        this.projects = projectsResponse.map((p) => ({
          ...p,
          Label: `${p.Title} (${p.ProjectOid}) - ${p.PlanType}`,
        } as ProjectListRecord));
      }

      // Load blueprints and demands
      const [blueprintsResponse, demandsResponse] = await Promise.all([
        this.apiService.getCapacityBlueprints().toPromise(),
        this.apiService.getDemands().toPromise(),
      ]);
      if (blueprintsResponse) {
        this.blueprints = blueprintsResponse;
      }
      if (demandsResponse) {
        this.demands = demandsResponse;
      }
    } catch (error: any) {
      this.notificationService.error(`Failed to load data: ${error.message}`);
    } finally {
      this.loadingService.setLoading(false);
      this.cdr.detectChanges();
    }
  }

  onGridReady(params: GridReadyEvent) {
    super.onGridReady(params);
    this.gridApi = params.api;
    // Hide loading overlay and show empty state since no project is selected yet
    this.gridApi.hideOverlay();
  }

  refreshData() {
    this.loadingService.setLoading(true);
    this.onProjectChanged();
    this.loadingService.setLoading(false);
  }

  onProjectChanged(): void {
    this.rowData = [];
    if (this.selectedProject) {
      // Exclude Removed demands so a removed demand's blueprint is not treated as
      // still assigned (which would block re-selecting it for the same project).
      const assignedBlueprintIds = new Set(
        this.demands
          .filter((d) => d.ProjectId === this.selectedProject.Id && d.Status !== "Archived")
          .map((d) => d.BlueprintId)
      );
      this.rowData = this.blueprints
        .map((blueprint) => ({
          ...blueprint,
          Assigned: assignedBlueprintIds.has(blueprint.Id),
        }));

      this.hasRowSelected = false;
      this.canAssign = false;
      this.cdr.detectChanges();

      setTimeout(() => {
        this.gridApi?.autoSizeAllColumns();
      }, 100);
    }
  }

  onSelectionChanged(params: SelectionChangedEvent): void {
    const rows = params.api.getSelectedRows().length;
    this.hasRowSelected = rows > 0;
    this.canAssign = this.hasRowSelected;
    this.cdr.detectChanges();
  }

  async onAssignBlueprints(): Promise<void> {
    const selectedRows = this.gridApi.getSelectedRows() as RowDataForBlueprint[];
    if (selectedRows.length === 0) {
      this.notificationService.warning("Please select at least one blueprint to assign.");
      return;
    }

    try {
      // Call assign API for all blueprints
      this.loadingService.setLoading(true);
      for (const row of selectedRows) {
        await this.apiService
          .assignCapacityBlueprintToProject(row.Id, this.selectedProject.Id.toString())
          .toPromise();
      }
      this.loadingService.setLoading(false);

      this.notificationService.success(
        `${selectedRows.length} blueprint(s) assigned to project ${this.selectedProject.Label.toString()} successfully.`
      );

      // Refresh demands and grid after assignment
      const demandsResponse = await this.apiService.getDemands().toPromise();
      if (demandsResponse) {
        this.demands = demandsResponse;
      }
      this.onProjectChanged();
    } catch (error: any) {
      this.loadingService.setLoading(false);
      this.notificationService.error(`Failed to assign blueprints: ${error.message}`);
    }
  }

  private async showPreviewModal(blueprintPreviews: BlueprintPreviewItem[], projectOid: string): Promise<boolean> {
    const modalRef = this.ngbModal.open(CapacityBlueprintAssignPreviewModalComponent, {
      backdrop: "static",
      size: "lg",
    });

    const component = modalRef.componentInstance as CapacityBlueprintAssignPreviewModalComponent;
    component.blueprintPreviews = blueprintPreviews;
    component.projectOid = projectOid;

    try {
      const result = await modalRef.result;
      return result === true;
    } catch {
      // Modal was dismissed
      return false;
    }
  }

  @HostListener("window:resize")
  onResize() {
    setTimeout(() => {
      this.gridApi?.autoSizeAllColumns();
    }, 100);
  }
}
